import type {
  VisualPayload,
  SeasonOverview,
  MoveChange,
} from "../../types";
import CardShell from "./_CardShell";
import {
  SeasonHeaderStrip,
  SeasonSourceLink,
  ChangeArrow,
  MoveChangeRow,
  changeDirection,
} from "./_seasonShared";

interface Props { payload: VisualPayload }

/**
 * Focused move-change render.
 *
 * Backend emits the matched-move(s) in section "move_changes".  When
 * the user asked about a specific move, expect 1–2 matches and surface
 * them prominently with big before→after numbers.  When the user
 * asked broadly OR their move wasn't changed, the backend falls back
 * to "all changes this season" and we render those compactly.
 *
 * The featured/spotlight render fires when there's exactly 1–3 changes
 * (the focused case); the compact list fires for the broad case.
 */
export default function MoveChangeCard({ payload }: Props) {
  const sections = payload.sections ?? [];
  const overview = (sections.find((s) => s.section === "season_overview") ?? {}) as SeasonOverview;
  const moveChangesSec = sections.find((s) => s.section === "move_changes");
  const changes = (moveChangesSec?.items as MoveChange[] | undefined) ?? [];

  if (changes.length === 0) {
    return (
      <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined} accent="border-indigo-500/20">
        <SeasonHeaderStrip overview={overview} />
        <p className="text-sm text-white/40 py-2">No move balance changes announced this season.</p>
        <SeasonSourceLink sourceUrl={payload.source_url} />
      </CardShell>
    );
  }

  // Focused render: 1-3 matched moves → spotlight layout with big numbers.
  // Broad render: >3 changes → assume "all changes this season" fallback,
  // render compactly with the shared MoveChangeRow.
  const isFocused = changes.length <= 3;

  return (
    <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined} accent="border-indigo-500/20">
      <SeasonHeaderStrip overview={overview} />

      {isFocused ? (
        <div className="space-y-3">
          {changes.map((c, i) => (
            <FocusedMoveChange key={i} c={c} />
          ))}
        </div>
      ) : (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-widest text-white/40 mb-2">
            All move changes this season ({changes.length})
          </p>
          <ul className="space-y-1">
            {changes.map((c, i) => (
              <MoveChangeRow key={i} c={c} />
            ))}
          </ul>
        </div>
      )}

      <SeasonSourceLink sourceUrl={payload.source_url} />
    </CardShell>
  );
}

function FocusedMoveChange({ c }: { c: MoveChange }) {
  const dir = changeDirection(c);
  const dirLabel = dir === "buff" ? "Buffed" : dir === "nerf" ? "Nerfed" : "Changed";
  const dirCls =
    dir === "buff"
      ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/25"
      : dir === "nerf"
      ? "bg-red-500/15 text-red-300 border-red-500/25"
      : "bg-white/10 text-white/60 border-white/15";

  return (
    <div className="bg-white/[0.04] border border-white/[0.06] rounded-lg p-3">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <p className="text-base font-semibold text-white leading-tight break-words">
            {c.move_name ?? "(unknown move)"}
          </p>
          <p className="text-[11px] text-white/40 mt-0.5 capitalize">
            {(c.change_kind ?? "").replace(/_/g, " ").replace(/^move /, "")}
          </p>
        </div>
        <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${dirCls} shrink-0`}>
          {dirLabel}
        </span>
      </div>

      {c.before != null && c.after != null ? (
        <div className="flex items-center justify-center gap-3 my-2">
          <span className="text-lg text-white/55 tabular-nums line-through">{String(c.before)}</span>
          <ChangeArrow direction={dir} />
          <span className={`text-2xl font-bold tabular-nums ${
            dir === "buff" ? "text-emerald-300" : dir === "nerf" ? "text-red-300" : "text-white/85"
          }`}>
            {String(c.after)}
          </span>
        </div>
      ) : (
        <p className="text-sm text-white/65 italic text-center my-2">
          {c.notes ?? "Value change details not provided"}
        </p>
      )}

      {c.notes && c.before != null && c.after != null && (
        <p className="text-[11px] text-white/45 mt-2 italic text-center">{c.notes}</p>
      )}
    </div>
  );
}
